/**
 * GDAP Relationships management page per COMP-007 Story 5.
 * Admin-only: list, create, terminate GDAP relationships.
 */

import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { AlertTriangle, Check, Link, Plus, Search, Shield, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "@/components/sc/glass-card";
import { StatusBadge } from "@/components/sc/status-badge";
import { EmptyState } from "@/components/sc/empty-state";
import { DataTable, type Column, type SortState } from "@/components/sc/data-table";
import {
  useGdapRelationships,
  useCreateRelationship,
  useReconcileRelationships,
} from "@/hooks/use-gdap";
import { useCustomerList } from "@/hooks/use-customers";
import { TEAMS_SPECIALIST_DEFAULT_ROLES, ENTRA_ROLE_DISPLAY_NAMES, type GDAPRelationship } from "@/types";

function relativeDate(iso: string): string {
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days < 0) {
    const absDays = Math.abs(days);
    if (absDays <= 30) return `in ${absDays}d`;
    return date.toLocaleDateString();
  }
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}

function getDurationDays(row: Record<string, unknown>): number {
  // API returns durationDays (number) from SQL, not ISO duration string
  if (typeof row.durationDays === "number") return row.durationDays;
  // Fallback: parse ISO 8601 duration if present
  const duration = row.duration;
  if (typeof duration === "string") {
    const match = duration.match(/P(\d+)D/);
    return match ? Number(match[1]) : 0;
  }
  return 0;
}

function daysUntil(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

const COLUMNS: Column<GDAPRelationship>[] = [
  {
    key: "customerTenantName",
    header: "Customer",
    sortable: true,
    className: "max-w-[200px]",
    render: (row) => (
      <span className="font-medium truncate block" title={row.customerTenantName}>
        {row.customerTenantName}
      </span>
    ),
  },
  {
    key: "id",
    header: "Relationship ID",
    render: (row) => (
      <span className="font-mono text-label text-sc-text-muted" title={row.id}>
        {row.id.slice(0, 12)}...
      </span>
    ),
  },
  {
    key: "status",
    header: "Status",
    sortable: true,
    render: (row) => <StatusBadge status={row.status} category="gdap" />,
  },
  {
    key: "duration",
    header: "Duration",
    render: (row) => (
      <span className="text-body-sm text-sc-text">
        {getDurationDays(row as unknown as Record<string, unknown>)}d
      </span>
    ),
  },
  {
    key: "roleCount",
    header: "Roles",
    render: (row) => {
      const count = (row as unknown as Record<string, number>).roleCount ?? 0;
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-badge font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
          {count}
        </span>
      );
    },
  },
  {
    key: "createdDateTime",
    header: "Created",
    sortable: true,
    render: (row) => (
      <span className="text-body-sm text-sc-text-muted">
        {row.createdDateTime ? relativeDate(row.createdDateTime) : "—"}
      </span>
    ),
  },
  {
    key: "endDateTime",
    header: "Expires",
    sortable: true,
    render: (row) => {
      if (!row.endDateTime) return <span className="text-body-sm text-sc-text-muted">—</span>;
      const days = daysUntil(row.endDateTime);
      return (
        <span
          className={`text-body-sm ${
            days <= 30 && days > 0
              ? "text-amber-600 dark:text-amber-400 font-medium"
              : days <= 0
                ? "text-sc-danger"
                : "text-sc-text-muted"
          }`}
        >
          {days > 0 ? `${days}d left` : "Expired"}
        </span>
      );
    },
  },
];

// Status filter tabs
const STATUS_TABS = [
  { label: "All", value: "" },
  { label: "Active", value: "Active" },
  { label: "Approving", value: "Approving" },
  { label: "Creating", value: "Creating" },
  { label: "Terminated", value: "Terminated" },
  { label: "Expired", value: "Expired" },
] as const;

export function GdapRelationshipsPage() {
  const navigate = useNavigate();
  const { createFor } = useSearch({ strict: false }) as { createFor?: string };
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sort, setSort] = useState<SortState>({
    column: "customerTenantName",
    direction: "asc",
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForCustomerId, setCreateForCustomerId] = useState<string | undefined>(undefined);

  // Handle createFor search param — auto-open create form with customer pre-selected
  const createForHandled = useRef(false);
  useEffect(() => {
    if (createFor && !createForHandled.current) {
      createForHandled.current = true;
      setCreateForCustomerId(createFor);
      setShowCreateForm(true);
      // Clear the search param from the URL
      void navigate({ to: "/gdap", search: { createFor: undefined }, replace: true });
    }
  }, [createFor, navigate]);

  // Debounce search input (300ms)
  const debounceRef = useMemo(() => {
    let timeout: ReturnType<typeof setTimeout>;
    return (value: string) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setDebouncedSearch(value);
        setPage(1);
      }, 300);
    };
  }, []);

  const gdapQuery = useGdapRelationships({
    page,
    pageSize,
    status: statusFilter || undefined,
    search: debouncedSearch || undefined,
  });
  const reconcileMutation = useReconcileRelationships();

  const relationships = gdapQuery.data?.items ?? [];
  const total = gdapQuery.data?.total ?? 0;

  return (
    <div className="space-y-4">
      {/* Search and filters bar */}
      <GlassCard className="flex flex-wrap items-center gap-3 !p-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sc-text-muted" />
          <input
            placeholder="Search relationships..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              debounceRef(e.target.value);
            }}
            className="h-9 w-full rounded-input border border-sc-border bg-sc-input-bg pl-9 pr-3 text-body-sm text-sc-text placeholder:text-sc-text-muted"
          />
        </div>
        <div className="flex items-center gap-2">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => {
                setStatusFilter(tab.value);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-btn text-body-sm font-medium transition-colors ${
                statusFilter === tab.value
                  ? "bg-sc-primary text-white"
                  : "text-sc-text-muted hover:text-sc-text hover:bg-sc-surface-hover border border-sc-border"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => {
              reconcileMutation.mutate(undefined, {
                onSuccess: (result) => {
                  toast.success(
                    `Reconciliation complete: ${result.reconciled} checked, ${result.drifts} drift(s) found`,
                  );
                },
                onError: () => {
                  toast.error("Reconciliation failed. Try again.");
                },
              });
            }}
            disabled={reconcileMutation.isPending}
            className="px-3 py-1.5 rounded-btn text-body-sm font-medium text-sc-text border border-sc-border hover:bg-sc-surface-hover transition-colors disabled:opacity-50"
          >
            {reconcileMutation.isPending ? "Reconciling..." : "Reconcile"}
          </button>
          <button
            onClick={() => {
              setCreateForCustomerId(undefined);
              setShowCreateForm(true);
            }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-btn text-body-sm font-medium bg-sc-primary text-white hover:bg-sc-primary/90 transition-colors"
          >
            <Plus size={16} />
            Create Relationship
          </button>
        </div>
      </GlassCard>

      {/* Create form — z-10 so dropdown renders above the table card below */}
      {showCreateForm && (
        <div className="relative z-10">
        <CreateRelationshipForm
          onClose={() => {
            setShowCreateForm(false);
            setCreateForCustomerId(undefined);
          }}
          preselectedCustomerId={createForCustomerId}
        />
        </div>
      )}

      {/* Table */}
      <DataTable<GDAPRelationship>
        columns={COLUMNS}
        data={relationships}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        sort={sort}
        onSortChange={setSort}
        isLoading={gdapQuery.isLoading}
        rowKey={(row) => row.id}
        onRowClick={(row) => {
          void navigate({
            to: "/gdap/$relationshipId",
            params: { relationshipId: row.id },
          });
        }}
        emptyState={
          <EmptyState
            icon={<Link size={48} />}
            heading="No GDAP relationships"
            description="Create your first relationship from the Customers page."
            action={
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center gap-1 mx-auto px-4 py-2 rounded-btn text-body-sm font-medium bg-sc-primary text-white hover:bg-sc-primary/90 transition-colors"
              >
                <Plus size={16} />
                Create Relationship
              </button>
            }
          />
        }
      />
    </div>
  );
}

// ---- Create Relationship Form (Story 5 Step 3) ----

function CreateRelationshipForm({
  onClose,
  preselectedCustomerId,
}: {
  readonly onClose: () => void;
  readonly preselectedCustomerId?: string;
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedCustomerId, setSelectedCustomerId] = useState(preselectedCustomerId ?? "");
  const [durationDays, setDurationDays] = useState(730);
  const [customerSearch, setCustomerSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const customersQuery = useCustomerList({ pageSize: 500 });
  const gdapQuery = useGdapRelationships({ page: 1, pageSize: 500 });
  const createMutation = useCreateRelationship();

  const customers = [...(customersQuery.data?.items ?? [])].sort((a, b) =>
    a.customerName.localeCompare(b.customerName),
  );
  const selectedCustomer = customers.find(
    (c) => c.m365TenantId === selectedCustomerId,
  );

  // Build a set of customer tenant IDs that already have an active/approving/creating relationship
  const existingRelationshipMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const rel of gdapQuery.data?.items ?? []) {
      const status = rel.status;
      if (status === "Active" || status === "Approving" || status === "Creating") {
        map.set(rel.customerTenantId, status);
      }
    }
    return map;
  }, [gdapQuery.data?.items]);

  // Filter and sort customers: no existing relationship first, then with relationship, each group alphabetical
  const filteredCustomers = useMemo(() => {
    let list = customers;
    if (customerSearch) {
      const lower = customerSearch.toLowerCase();
      list = list.filter(
        (c) =>
          c.customerName.toLowerCase().includes(lower) ||
          c.m365TenantId.toLowerCase().includes(lower),
      );
    }
    return [...list].sort((a, b) => {
      const aHas = existingRelationshipMap.has(a.m365TenantId) ? 1 : 0;
      const bHas = existingRelationshipMap.has(b.m365TenantId) ? 1 : 0;
      if (aHas !== bHas) return aHas - bHas;
      return a.customerName.localeCompare(b.customerName);
    });
  }, [customers, customerSearch, existingRelationshipMap]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // If preselected customer is set and customers are loaded, apply the duration override
  useEffect(() => {
    if (preselectedCustomerId && customers.length > 0) {
      const cust = customers.find((c) => c.m365TenantId === preselectedCustomerId);
      if (cust?.durationDays) setDurationDays(cust.durationDays);
    }
  }, [preselectedCustomerId, customers]);

  function selectCustomer(tenantId: string) {
    setSelectedCustomerId(tenantId);
    setDropdownOpen(false);
    setCustomerSearch("");
    const cust = customers.find((c) => c.m365TenantId === tenantId);
    if (cust?.durationDays) setDurationDays(cust.durationDays);
  }

  function handleSubmit() {
    if (!selectedCustomerId) return;
    createMutation.mutate(
      { customerTenantId: selectedCustomerId, durationDays },
      {
        onSuccess: (result) => {
          toast.success("GDAP relationship created — awaiting customer approval");
          onClose();
          void navigate({
            to: "/gdap/$relationshipId",
            params: { relationshipId: result.id },
          });
        },
        onError: (err) => {
          toast.error(
            `Failed to create relationship: ${err instanceof Error ? err.message : "Unknown error"}`,
          );
        },
      },
    );
  }

  return (
    <GlassCard className="overflow-visible">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield size={18} className="text-sc-primary" />
          <h3 className="text-h3 font-display text-sc-navy">
            Create GDAP Relationship
          </h3>
        </div>
        <button
          onClick={onClose}
          className="text-body-sm text-sc-text-muted hover:text-sc-text"
        >
          Cancel
        </button>
      </div>

      {/* Step indicators */}
      <div className="flex items-center gap-2 mb-6">
        {[1, 2, 3, 4].map((s) => (
          <div
            key={s}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              s <= step ? "bg-sc-primary" : "bg-sc-border"
            }`}
          />
        ))}
      </div>

      {/* Step 1: Select customer */}
      {step === 1 && (
        <div className="space-y-4">
          <label className="text-body-sm text-sc-text font-medium">
            Select Customer
          </label>
          {customersQuery.isLoading ? (
            <p className="text-body-sm text-sc-text-muted">
              Loading customers...
            </p>
          ) : (
            <div className="relative" ref={dropdownRef}>
              {/* Selected customer display / search input */}
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sc-text-muted" />
                <input
                  placeholder="Search customers..."
                  value={dropdownOpen ? customerSearch : (selectedCustomer?.customerName ?? customerSearch)}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    if (!dropdownOpen) setDropdownOpen(true);
                    if (selectedCustomerId && e.target.value !== selectedCustomer?.customerName) {
                      setSelectedCustomerId("");
                    }
                  }}
                  onFocus={() => setDropdownOpen(true)}
                  className="w-full h-9 rounded-input border border-sc-border bg-sc-input-bg pl-9 pr-3 text-body-sm text-sc-text placeholder:text-sc-text-muted"
                />
              </div>
              {/* Dropdown list */}
              {dropdownOpen && (
                <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-card border border-sc-border bg-sc-surface shadow-lg">
                  {filteredCustomers.length === 0 ? (
                    <div className="px-3 py-2 text-body-sm text-sc-text-muted">
                      No customers found
                    </div>
                  ) : (
                    filteredCustomers.map((c) => {
                      const existingStatus = existingRelationshipMap.get(c.m365TenantId);
                      const isSelected = c.m365TenantId === selectedCustomerId;
                      return (
                        <button
                          key={c.m365TenantId}
                          onClick={() => selectCustomer(c.m365TenantId)}
                          className={`w-full text-left px-3 py-2 text-body-sm transition-colors flex items-center justify-between gap-2 ${
                            isSelected
                              ? "bg-sc-primary/10 text-sc-text"
                              : "text-sc-text hover:bg-sc-surface-hover"
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <span className="font-medium truncate block">
                              {c.customerName}
                            </span>
                            <span className="text-label text-sc-text-muted">
                              {c.accessType}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {existingStatus && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                <Check size={10} />
                                {existingStatus}
                              </span>
                            )}
                            {isSelected && (
                              <Check size={14} className="text-sc-primary" />
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end">
            <button
              onClick={() => setStep(2)}
              disabled={!selectedCustomerId}
              className="px-4 py-2 rounded-btn text-body-sm font-medium bg-sc-primary text-white hover:bg-sc-primary/90 disabled:opacity-50 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Duration */}
      {step === 2 && (
        <div className="space-y-4">
          <label className="text-body-sm text-sc-text font-medium">
            Duration (days)
          </label>
          <input
            type="number"
            min={1}
            max={730}
            value={durationDays}
            onChange={(e) => setDurationDays(Number(e.target.value))}
            className="w-full h-9 rounded-input border border-sc-border bg-sc-input-bg px-3 text-body-sm text-sc-text"
          />
          <p className="text-label text-sc-text-muted">
            Default: 730 days.{" "}
            {selectedCustomer?.durationDays &&
              `Customer override: ${selectedCustomer.durationDays} days.`}
          </p>
          <div className="flex justify-between">
            <button
              onClick={() => setStep(1)}
              className="px-4 py-2 rounded-btn text-body-sm text-sc-text-muted hover:text-sc-text"
            >
              Back
            </button>
            <button
              onClick={() => setStep(3)}
              className="px-4 py-2 rounded-btn text-body-sm font-medium bg-sc-primary text-white hover:bg-sc-primary/90 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Review role bundle */}
      {step === 3 && selectedCustomer && (
        <div className="space-y-4">
          {selectedCustomer.accessType === "TEAMS" ? (
            <>
              <p className="text-body-sm text-sc-text">
                This is a <strong>Teams Specialist</strong> relationship.
                Only the Teams Specialist role bundle will be provisioned.
              </p>
              <div className="p-3 rounded-card bg-sc-surface-hover border border-sc-border">
                <span className="text-label text-sc-text-muted uppercase tracking-widest">
                  Teams Specialist Roles
                </span>
                <div className="mt-2 space-y-1">
                  {TEAMS_SPECIALIST_DEFAULT_ROLES.map((roleId) => (
                    <div key={roleId} className="flex items-center gap-2 text-body-sm text-sc-text">
                      <span className="h-1.5 w-1.5 rounded-full bg-sc-primary shrink-0" />
                      {ENTRA_ROLE_DISPLAY_NAMES[roleId as keyof typeof ENTRA_ROLE_DISPLAY_NAMES] ?? roleId}
                    </div>
                  ))}
                </div>
              </div>
              <GlassCard className="border-teal-500/30 bg-teal-900/10">
                <div className="flex items-start gap-3">
                  <ShieldCheck size={20} className="text-teal-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-body-sm font-medium text-sc-navy dark:text-sc-text">
                      Minimal role set — no Global Administrator
                    </p>
                    <p className="text-body-sm text-sc-text-muted mt-1">
                      This relationship includes only {TEAMS_SPECIALIST_DEFAULT_ROLES.length} roles
                      and will auto-renew every 180 days. Only 1 group assignment
                      (GDAP-TeamsSpecialist) will be provisioned after approval.
                    </p>
                  </div>
                </div>
              </GlassCard>
            </>
          ) : (
            <>
              <p className="text-body-sm text-sc-text">
                Role bundle will be auto-selected based on customer strategy (
                <strong>{selectedCustomer.accessType}</strong>) and max access level
                (<strong>{selectedCustomer.maxAccessLevel}</strong>).
              </p>
              <div className="p-3 rounded-card bg-sc-surface-hover border border-sc-border">
                <span className="text-label text-sc-text-muted uppercase tracking-widest">
                  Max Access Level
                </span>
                <p className="text-body text-sc-text font-medium mt-1">
                  {selectedCustomer.maxAccessLevel}
                </p>
              </div>
              <GlassCard className="border-amber-500/30 bg-amber-900/10">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-body-sm font-medium text-sc-navy dark:text-sc-text">
                      Includes Global Administrator
                    </p>
                    <p className="text-body-sm text-sc-text-muted mt-1">
                      Auto-renewal is not available for this relationship. When it
                      expires, a new relationship will be created and the customer
                      must re-approve it in the M365 Admin Center.
                    </p>
                  </div>
                </div>
              </GlassCard>
            </>
          )}
          <div className="flex justify-between">
            <button
              onClick={() => setStep(2)}
              className="px-4 py-2 rounded-btn text-body-sm text-sc-text-muted hover:text-sc-text"
            >
              Back
            </button>
            <button
              onClick={() => setStep(4)}
              className="px-4 py-2 rounded-btn text-body-sm font-medium bg-sc-primary text-white hover:bg-sc-primary/90 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Confirm */}
      {step === 4 && selectedCustomer && (
        <div className="space-y-4">
          <h4 className="text-body text-sc-text font-medium">Confirm</h4>
          <div className="space-y-2 text-body-sm">
            <div className="flex justify-between">
              <span className="text-sc-text-muted">Customer</span>
              <span className="text-sc-text font-medium">
                {selectedCustomer.customerName}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sc-text-muted">Tenant ID</span>
              <span className="text-sc-text font-mono text-label">
                {selectedCustomer.m365TenantId}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sc-text-muted">Duration</span>
              <span className="text-sc-text">{durationDays} days</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sc-text-muted">Strategy</span>
              <StatusBadge
                status={selectedCustomer.accessType}
                category="accessType"
              />
            </div>
            <div className="flex justify-between">
              <span className="text-sc-text-muted">Renewal</span>
              <span className="text-amber-400 text-body-sm font-medium">
                Manual (requires customer re-approval)
              </span>
            </div>
          </div>
          <div className="flex justify-between">
            <button
              onClick={() => setStep(3)}
              className="px-4 py-2 rounded-btn text-body-sm text-sc-text-muted hover:text-sc-text"
            >
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={createMutation.isPending}
              className="px-4 py-2 rounded-btn text-body-sm font-medium bg-sc-primary text-white hover:bg-sc-primary/90 disabled:opacity-50 transition-colors"
            >
              {createMutation.isPending ? "Creating..." : "Create"}
            </button>
          </div>
        </div>
      )}
    </GlassCard>
  );
}
